import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  ChevronLeft,
  ChevronRight,
  PanelRightOpen,
  MoreHorizontal,
} from "lucide-react";
import { useAppContext } from "../../contexts/AppContext";
import {
  format,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  parseISO,
  isValid,
} from "date-fns";
import { useFilesByDate } from "../../hooks/useFilesByDate";
import { useFilesByDateTime } from "../../hooks/useFilesByDateTime";
import { useFileListProjection } from "../../hooks/useFileListProjection";
import { useIsMobile } from "../../hooks/useMobile";
import { useCalendarDrag } from "./useCalendarDrag";
import MonthlyGrid from "./MonthlyGrid";
import WeeklyGrid from "./WeeklyGrid";
import CalendarViewToggle from "./CalendarViewToggle";
import { HeaderIconButton } from "../Header/HeaderActions";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@exiftrator/shared/components/ui";
import { PanelHeader } from "../PanelHeader";
import TimePickerContent from "../ContextPanel/TimePickerContent";
import ThreePaneWorkspace from "../Workspace/ThreePaneWorkspace";
import { WORKSPACE_VIEW_META } from "../Workspace/workspaceViewMeta";
import StandardQueuePane from "../FileQueue/StandardQueuePane";
import FileListControls from "../FileQueue/FileListControls";
import { ViewIndicator } from "../ViewIndicator";
import {
  OVERFLOW_MENU_CONTENT_CLASS,
  OVERFLOW_MENU_ITEM_CLASS,
  OVERFLOW_MENU_SHORTCUT_CLASS,
} from "../Header/overflowMenuStyles";

function CalendarView() {
  const calendarViewMeta = WORKSPACE_VIEW_META.calendar;
  const {
    files,
    fileListSort,
    setFileListSort,
    fileListFilters,
    setFileListFilters,
    calendarSelectedDate,
    setCalendarSelectedDate,
    calendarSelectedHour,
    setCalendarSelectedHour,
    setCalendarPanelExpanded,
    calendarViewMode,
    setCalendarViewMode,
    timezone,
    calendarRightPanelOpen,
    setCalendarRightPanelOpen,
    calendarMobileLeftOpen,
    setCalendarMobileLeftOpen,
    calendarMobileRightOpen,
    setCalendarMobileRightOpen,
    setKeyboardShortcutsModalOpen,
    setKeyboardShortcutsInitialView,
  } = useAppContext();
  const { visibleIndices } = useFileListProjection({
    files,
    sort: fileListSort,
    filters: fileListFilters,
  });
  const visibleFiles = useMemo(
    () => visibleIndices.map((index) => files[index]).filter(Boolean),
    [files, visibleIndices],
  );
  const { grouped } = useFilesByDate(visibleFiles, visibleIndices);
  const { groupedByHour, groupedByDate } = useFilesByDateTime(
    visibleFiles,
    visibleIndices,
  );

  const isMobile = useIsMobile();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const activeCalendarMode =
    calendarViewMode === "weekly" ? "weekly" : "monthly";
  const hasVisitedMonthlyRef = useRef(false);
  const hasVisitedWeeklyRef = useRef(false);

  const newestTimestampDate = useMemo(() => {
    let newestTimestamp = Number.NEGATIVE_INFINITY;

    for (const file of visibleFiles) {
      const dateTaken = file.modifiedExifData?.dateTaken;
      if (!dateTaken) continue;

      const parsedDate = parseISO(dateTaken);
      if (!isValid(parsedDate)) continue;

      newestTimestamp = Math.max(newestTimestamp, parsedDate.getTime());
    }

    return Number.isFinite(newestTimestamp) ? new Date(newestTimestamp) : null;
  }, [visibleFiles]);

  const initializeMonthlyIfNeeded = useCallback(() => {
    if (hasVisitedMonthlyRef.current) return false;
    setCurrentMonth(
      newestTimestampDate ? new Date(newestTimestampDate) : new Date(),
    );
    hasVisitedMonthlyRef.current = true;
    return true;
  }, [newestTimestampDate]);

  const initializeWeeklyIfNeeded = useCallback(() => {
    if (hasVisitedWeeklyRef.current) return false;
    setCurrentWeek(
      newestTimestampDate ? new Date(newestTimestampDate) : new Date(),
    );
    hasVisitedWeeklyRef.current = true;
    return true;
  }, [newestTimestampDate]);

  const {
    dragOverTarget,
    handleFileDragStart,
    handleDragOverDate,
    handleDragOverTimeSlot,
    handleDragLeave,
    handleDropToDate,
    handleDropToTimeSlot,
  } = useCalendarDrag();

  // Handle view mode change - sync the dates between views
  const handleViewModeChange = useCallback(
    (newMode) => {
      if (newMode !== "monthly" && newMode !== "weekly") return;

      if (newMode === "weekly") {
        const wasInitialized = initializeWeeklyIfNeeded();

        if (!wasInitialized && activeCalendarMode === "monthly") {
          // Switching from monthly to weekly: use start of the displayed month
          setCurrentWeek(startOfMonth(currentMonth));
        }
      } else if (newMode === "monthly") {
        const wasInitialized = initializeMonthlyIfNeeded();

        if (!wasInitialized && activeCalendarMode === "weekly") {
          // Switching from weekly to monthly: use the month containing current week
          setCurrentMonth(currentWeek);
        }
      }

      setCalendarViewMode(newMode);
    },
    [
      activeCalendarMode,
      currentMonth,
      currentWeek,
      initializeMonthlyIfNeeded,
      initializeWeeklyIfNeeded,
      setCalendarViewMode,
    ],
  );

  // Go to today
  const goToToday = useCallback(() => {
    const today = new Date();
    setCurrentMonth(today);
    setCurrentWeek(today);
  }, []);

  const goToMostRecent = useCallback(() => {
    if (!newestTimestampDate) return;

    setCurrentMonth(new Date(newestTimestampDate));
    setCurrentWeek(new Date(newestTimestampDate));
  }, [newestTimestampDate]);

  // Monthly navigation
  const goToPrevMonth = useCallback(() => {
    setCurrentMonth((prev) => subMonths(prev, 1));
  }, []);

  const goToNextMonth = useCallback(() => {
    setCurrentMonth((prev) => addMonths(prev, 1));
  }, []);

  // Weekly navigation
  const goToPrevWeek = useCallback(() => {
    setCurrentWeek((prev) => subWeeks(prev, 1));
  }, []);

  const goToNextWeek = useCallback(() => {
    setCurrentWeek((prev) => addWeeks(prev, 1));
  }, []);

  // Click handlers - allow clicking any date to open the panel
  const handleCellClick = useCallback(
    (day, event) => {
      const dateKey = format(day, "yyyy-MM-dd");
      setCalendarSelectedDate(dateKey);
      setCalendarSelectedHour(null); // Monthly view doesn't filter by hour
      setCalendarPanelExpanded(true);
      setCalendarRightPanelOpen(true);
      if (isMobile) setCalendarMobileRightOpen(true);
    },
    [
      setCalendarSelectedDate,
      setCalendarSelectedHour,
      setCalendarPanelExpanded,
      setCalendarRightPanelOpen,
      isMobile,
      setCalendarMobileRightOpen,
    ],
  );

  const handleSlotClick = useCallback(
    (day, hour, event) => {
      setCalendarSelectedDate(format(day, "yyyy-MM-dd"));
      setCalendarSelectedHour(hour);
      setCalendarPanelExpanded(true);
      setCalendarRightPanelOpen(true);
      if (isMobile) setCalendarMobileRightOpen(true);
    },
    [
      setCalendarSelectedDate,
      setCalendarSelectedHour,
      setCalendarPanelExpanded,
      setCalendarRightPanelOpen,
      isMobile,
      setCalendarMobileRightOpen,
    ],
  );

  const ensurePanelSelection = useCallback(() => {
    if (!calendarSelectedDate) {
      setCalendarSelectedDate(format(new Date(), "yyyy-MM-dd"));
      setCalendarSelectedHour(null);
    }
    setCalendarPanelExpanded(true);
  }, [
    calendarSelectedDate,
    setCalendarSelectedDate,
    setCalendarSelectedHour,
    setCalendarPanelExpanded,
  ]);

  const handleDesktopRightToggle = useCallback(() => {
    if (calendarRightPanelOpen) {
      setCalendarRightPanelOpen(false);
      return;
    }

    ensurePanelSelection();
    setCalendarRightPanelOpen(true);
  }, [calendarRightPanelOpen, ensurePanelSelection, setCalendarRightPanelOpen]);

  const handleCloseRightPanel = useCallback(() => {
    setCalendarRightPanelOpen(false);
    setCalendarMobileRightOpen(false);
    setCalendarPanelExpanded(false);
  }, [
    setCalendarRightPanelOpen,
    setCalendarMobileRightOpen,
    setCalendarPanelExpanded,
  ]);

  // Navigation label and handlers based on view mode
  const navLabel =
    activeCalendarMode === "monthly"
      ? format(currentMonth, "MMMM yyyy")
      : `${format(startOfWeek(currentWeek), "MMM d")} - ${format(endOfWeek(currentWeek), "MMM d, yyyy")}`;

  const goToPrev =
    activeCalendarMode === "monthly" ? goToPrevMonth : goToPrevWeek;
  const goToNext =
    activeCalendarMode === "monthly" ? goToNextMonth : goToNextWeek;

  const containerRef = useRef(null);

  // Take focus on mount so keyboard navigation works immediately
  useEffect(() => {
    containerRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    if (calendarViewMode !== "monthly" && calendarViewMode !== "weekly") {
      setCalendarViewMode("monthly");
      return;
    }

    if (activeCalendarMode === "monthly") {
      initializeMonthlyIfNeeded();
      return;
    }

    initializeWeeklyIfNeeded();
  }, [
    activeCalendarMode,
    calendarViewMode,
    initializeMonthlyIfNeeded,
    initializeWeeklyIfNeeded,
    setCalendarViewMode,
  ]);

  useEffect(() => {
    if (!calendarRightPanelOpen && !calendarMobileRightOpen) return;
    ensurePanelSelection();
  }, [calendarRightPanelOpen, calendarMobileRightOpen, ensurePanelSelection]);

  const handleKeyDown = useCallback(
    (e) => {
      // Check if we're in an input field
      const isInInput =
        e.target.tagName === "INPUT" ||
        e.target.tagName === "TEXTAREA" ||
        e.target.isContentEditable;

      if (!isInInput) {
        if (e.key === "j" || e.key === "J") {
          e.preventDefault();
          goToPrev();
        } else if (e.key === "k" || e.key === "K") {
          e.preventDefault();
          goToNext();
        }
      }
    },
    [goToPrev, goToNext],
  );

  const filesForSelection = useMemo(() => {
    if (!calendarSelectedDate) return [];

    if (calendarSelectedHour !== null) {
      const hourKey = `${calendarSelectedDate}T${String(calendarSelectedHour).padStart(2, "0")}`;
      return groupedByHour.get(hourKey) || [];
    }

    return grouped.get(calendarSelectedDate) || [];
  }, [calendarSelectedDate, calendarSelectedHour, groupedByHour, grouped]);

  const panelDate = calendarSelectedDate || format(new Date(), "yyyy-MM-dd");
  const panelKey = `${panelDate}-${calendarSelectedHour ?? "all"}`;

  const rightPanelShowButton =
    !isMobile && !calendarRightPanelOpen ? (
      <HeaderIconButton
        intent="utility"
        onClick={handleDesktopRightToggle}
        aria-label="Show date/time panel"
        title="Show date/time panel"
      >
        <PanelRightOpen data-icon="inline-start" className="h-4 w-4" />
      </HeaderIconButton>
    ) : null;
  const listControls = (
    <FileListControls
      sort={fileListSort}
      onSortChange={setFileListSort}
      filters={fileListFilters}
      onFiltersChange={setFileListFilters}
    />
  );
  const handleOpenShortcuts = useCallback(() => {
    setKeyboardShortcutsInitialView("calendar");
    setKeyboardShortcutsModalOpen(true);
  }, [setKeyboardShortcutsInitialView, setKeyboardShortcutsModalOpen]);

  const centerPane = (
    <div
      ref={containerRef}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      className="flex-1 flex flex-col bg-bg-workspace overflow-hidden outline-none"
    >
      <PanelHeader
        tone="contrast"
        rightContent={listControls}
        title={
          <div className="flex items-center gap-2 sm:gap-4">
            <div className={!isMobile ? "min-w-[200px]" : ""}>{navLabel}</div>
            <div className="flex items-center gap-1">
              <HeaderIconButton
                intent="utility"
                onClick={goToPrev}
                aria-label="Previous"
              >
                <ChevronLeft data-icon="inline-start" />
              </HeaderIconButton>
              <HeaderIconButton
                intent="utility"
                onClick={goToNext}
                aria-label="Next"
              >
                <ChevronRight data-icon="inline-start" />
              </HeaderIconButton>
            </div>
            <Button variant="outline" size="sm" onClick={goToToday}>
              Today
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={goToMostRecent}
              disabled={!newestTimestampDate}
              title={
                newestTimestampDate
                  ? "Jump to the most recent photo datetime"
                  : "No photo datetime values available"
              }
            >
              Most recent
            </Button>
          </div>
        }
      >
        <div className="flex items-center gap-2">
          <CalendarViewToggle
            calendarMode={activeCalendarMode}
            onCalendarModeChange={handleViewModeChange}
          />
          {rightPanelShowButton}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <HeaderIconButton
                intent="utility"
                aria-label="Calendar options"
                title="Calendar options"
              >
                <MoreHorizontal data-icon="inline-start" className="h-4 w-4" />
              </HeaderIconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className={OVERFLOW_MENU_CONTENT_CLASS}
            >
              <DropdownMenuItem
                onClick={handleOpenShortcuts}
                className={OVERFLOW_MENU_ITEM_CLASS}
              >
                <span>Keyboard shortcuts</span>
                <DropdownMenuShortcut className={OVERFLOW_MENU_SHORTCUT_CLASS}>
                  ?
                </DropdownMenuShortcut>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </PanelHeader>

      <div className="flex-1 flex flex-col min-h-0">
        {activeCalendarMode === "monthly" ? (
          <MonthlyGrid
            currentMonth={currentMonth}
            grouped={grouped}
            dragOverTarget={dragOverTarget}
            onCellClick={handleCellClick}
            onDragOver={handleDragOverDate}
            onDragLeave={handleDragLeave}
            onDrop={handleDropToDate}
            onFileDragStart={handleFileDragStart}
          />
        ) : (
          <WeeklyGrid
            currentWeek={currentWeek}
            groupedByHour={groupedByHour}
            groupedByDate={groupedByDate}
            dragOverTarget={dragOverTarget}
            onSlotClick={handleSlotClick}
            onDragOver={handleDragOverTimeSlot}
            onDragLeave={handleDragLeave}
            onDrop={handleDropToTimeSlot}
            onFileDragStart={handleFileDragStart}
          />
        )}
      </div>
    </div>
  );

  const rightPane = (
    <TimePickerContent
      key={panelKey}
      date={panelDate}
      hour={calendarSelectedHour}
      timezone={timezone}
      files={filesForSelection}
      onClose={handleCloseRightPanel}
      inSheet={isMobile}
    />
  );

  return (
    <main className="relative flex h-full flex-1 flex-col bg-linear-to-br from-bg-primary to-bg-deep">
      <ThreePaneWorkspace
        isMobile={isMobile}
        leftPane={
          <StandardQueuePane
            horizontal={isMobile}
            headerContent={
              <ViewIndicator
                label={calendarViewMeta.label}
                icon={calendarViewMeta.icon}
              />
            }
            showListControls={false}
          />
        }
        centerPane={centerPane}
        rightPane={rightPane}
        desktopRightOpen={calendarRightPanelOpen}
        mobileLeftOpen={calendarMobileLeftOpen}
        onMobileLeftOpenChange={setCalendarMobileLeftOpen}
        mobileRightOpen={calendarMobileRightOpen}
        onMobileRightOpenChange={(open) => {
          setCalendarMobileRightOpen(open);
          if (!open) {
            setCalendarPanelExpanded(false);
          }
        }}
        mobileLeftSheetSide="bottom"
        mobileRightSheetSide="bottom"
      />
    </main>
  );
}

export default CalendarView;
